import styled, { css } from 'styled-components';
import { IconButton, Typography } from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';

import colors from '../../../../../styles/colors';
import GenericDataGrid from '../../../../components/GenericDataGrid';

import { TaskCellState } from '../../utils/acLeadUtils';

const ACLeadGrid = styled(GenericDataGrid)`
  /* The task columns are a matrix of single numbers, so they read as one block rather than as
     seven separate columns each padded like a text column. */
  .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer {
    justify-content: center;
    border-bottom-color: ${colors.shades.secondary7};
  }
`;

const cellTextStyles = css`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
`;

const NameText = styled(Typography)`
  ${cellTextStyles}
  font-weight: ${({ $isSubRow }) => ($isSubRow ? 400 : 600)};
  color: ${({ $isSubRow }) => ($isSubRow ? colors.shades.gamma2 : 'inherit')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** How many coaches an AC row covers, said quietly beside their name. */
const NameSubText = styled(Typography)`
  ${cellTextStyles}
  color: ${colors.shades.gamma2};
  white-space: nowrap;
  flex-shrink: 0;
`;

const SharedCoachIcon = styled(WarningAmberRounded)`
  color: ${colors.base.warning};
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

/**
 * How each cell state reads.
 *
 * Colour carries the grade, and the suspect band is deliberately **not** red: those are the items
 * the AC lead asked about, and the point of banding them is that they stop competing with this
 * week's breaches for attention. Weight does the rest of the work, so the states stay separable
 * for anyone reading the column without colour.
 */
const TASK_CELL_STYLES = {
  [TaskCellState.CRITICAL]: { color: colors.shades.danger3, weight: 600 },
  [TaskCellState.BREACHED]: { color: colors.base.warning, weight: 600 },
  [TaskCellState.SUSPECT]: { color: colors.shades.gamma10, weight: 500 },
  [TaskCellState.WITHIN_SLA]: { color: colors.shades.gamma3, weight: 400 },
  [TaskCellState.NONE]: { color: colors.shades.gamma3, weight: 400 },
  [TaskCellState.UNKNOWN_AGE]: { color: colors.shades.gamma14, weight: 600, decoration: 'underline' },
  [TaskCellState.EXCLUDED]: { color: colors.shades.gamma9, weight: 400, decoration: 'line-through' },
};

const getTaskCellStyle = ({ $state }) => TASK_CELL_STYLES[$state] ?? TASK_CELL_STYLES[TaskCellState.NONE];

/**
 * One number, and the whole cell is the link to that queue.
 *
 * Rendered as a span rather than an anchor where there is nothing to open, so an empty queue offers
 * no affordance it cannot honour.
 */
const TaskCount = styled.span`
  ${cellTextStyles}
  display: inline-block;
  min-width: 100%;
  color: ${(props) => getTaskCellStyle(props).color};
  font-weight: ${(props) => getTaskCellStyle(props).weight};
  /* Struck through where the filter took it out of scope, so the lead can see the mass they just
     removed rather than watching numbers disappear; dotted-underlined where the age is unknown, so
     a question mark reads as "not yet known" rather than as a typo. */
  text-decoration-line: ${({ $state }) => TASK_CELL_STYLES[$state]?.decoration ?? 'none'};
  text-decoration-style: ${({ $state }) => ($state === TaskCellState.UNKNOWN_AGE ? 'dotted' : 'solid')};

  &[href] {
    cursor: pointer;

    &:hover {
      text-decoration-line: underline;
    }
  }
`;

const PastDueCount = styled(Typography)`
  ${cellTextStyles}
  color: ${({ $isPastDue }) => ($isPastDue ? colors.shades.danger3 : colors.shades.gamma10)};
`;

/**
 * How far past SLA the worst wait on this row is.
 *
 * Suspect waits are greyed rather than reddened for the same reason the cells are: a five-year-old
 * item is not this week's problem, and colouring it as one buries the ones that are.
 */
const OverdueDuration = styled(Typography)`
  ${cellTextStyles}
  color: ${({ $isSuspect, $isCritical }) => {
    if ($isSuspect) {
      return colors.shades.gamma10;
    }

    return $isCritical ? colors.shades.danger3 : colors.base.warning;
  }};
  text-decoration: none;
  width: fit-content;

  &[href] {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ExpandButton = styled(IconButton)`
  flex-shrink: 0;
  padding: 2px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

/** Explains every marker in the matrix, in the footer where it is out of the way but always to hand. */
const Legend = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 14px;
  padding: 0 16px;
`;

const LegendItem = styled(Typography)`
  ${cellTextStyles}
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${colors.shades.gamma10};
  white-space: nowrap;
`;

const LegendSwatch = styled.span`
  ${cellTextStyles}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 10px;
  color: ${(props) => getTaskCellStyle(props).color};
  font-weight: ${(props) => getTaskCellStyle(props).weight};
`;

const LegendDot = styled(LegendSwatch)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => getTaskCellStyle(props).color};
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  border-top: 1px solid ${colors.shades.gamma1};
`;

export {
  ACLeadGrid,
  ExpandButton,
  FooterRow,
  Legend,
  LegendDot,
  LegendItem,
  LegendSwatch,
  NameCell,
  NameSubText,
  NameText,
  OverdueDuration,
  PastDueCount,
  SharedCoachIcon,
  TaskCount,
};
